import type { Money } from 'src/feature/CardDetailHero/cardDetailHeroDemoData'
import type {
  StatementDocumentFormat,
  StatementSummary,
  StatementTransaction,
} from 'src/page-components/Accounts/AccountStatementsTab/accountStatementsDemoData'
import type { CardManagementStatementDocument } from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import { CARD_MANAGEMENT_STATEMENT_DOCUMENT_MEDIA } from 'src/queries/physicalCardManagement/physicalCardManagement.models'

/**
 * Statement documents for DEMO statements (EX-40602).
 *
 * A demo statement has no server document — its id is fabricated, so the real
 * download route answers `404 CARD-MGMT.25` — yet the demo experience the
 * feature is built for has to produce a real file when Download is used. These
 * are written in the browser from the same figures the row and modal already
 * show, so the file and the screen can never disagree.
 *
 * ## Generic and unbranded, on purpose
 *
 * The output carries no logo, no brand colour, no bank name, and no typeface
 * beyond the PDF base-14 Helvetica every reader already has. This is the same
 * shape the API's own `DeterministicStatementPdf` produces, and the reason is
 * the same: a fabricated statement that LOOKS like an issued bank document is a
 * document someone can forward as one. Every page also carries a sample-data
 * line, for the same reason the tab badges the section on screen.
 *
 * Deterministic — no clock, no randomness — so the same statement always yields
 * the same bytes.
 *
 * XLSX is deliberately not generated: it is a zip container, this app carries
 * no spreadsheet library, and `DEMO_STATEMENT_DOCUMENT_FORMATS` therefore never
 * offers it. Delete this module when demo statements gain real documents.
 */

/** Stated on every generated document, in the file itself rather than only the UI. */
const SAMPLE_NOTICE = 'Sample data - not a real statement'

/* -------------------------------------------------------------------------- */
/* PDF                                                                        */
/* -------------------------------------------------------------------------- */

/**
 * Lines per page at 14pt leading from a 760pt baseline inside a 792pt page —
 * the last line lands at 74pt, clear of the bottom edge.
 */
const LINES_PER_PAGE = 48

/** Object numbers 1-3 are the catalog, page tree, and font. */
const FIRST_PAGE_OBJECT_NUMBER = 4

/** Each page contributes a page object and its content stream. */
const objectNumberForPage = (pageIndex: number): number =>
  FIRST_PAGE_OBJECT_NUMBER + pageIndex * 2

/**
 * Makes one line safe to write inside a PDF text string.
 *
 * `\`, `(`, and `)` terminate or nest a PDF string literal, so an unescaped
 * merchant name containing one produces a file no reader can parse. Non-ASCII
 * is dropped rather than encoded because the base-14 Helvetica encoding cannot
 * render it anyway, and because the byte offsets in the cross-reference table
 * below are computed from string length — which only equals the byte count
 * while the content stays ASCII.
 */
const toPdfText = (line: string): string =>
  line
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x20-\x7e]/g, '')
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)')

const pageContentStream = (lines: string[]): string =>
  `BT\n/F1 10 Tf\n50 760 Td\n${lines
    .map((line) => `(${toPdfText(line)}) Tj\n0 -14 Td\n`)
    .join('')}ET\n`

/**
 * Assembles a single-font, text-only PDF 1.4 with a valid cross-reference
 * table.
 *
 * The xref offsets are byte positions from the start of the file, which is why
 * every string above is held to ASCII: one multi-byte character would shift
 * every subsequent offset and leave a file that opens as damaged.
 */
const renderPdf = (lines: string[]): Blob => {
  const pages: string[][] = []
  for (let index = 0; index < lines.length; index += LINES_PER_PAGE) {
    pages.push(lines.slice(index, index + LINES_PER_PAGE))
  }
  if (pages.length === 0) {
    pages.push([])
  }

  const kids = pages
    .map((_, index) => `${objectNumberForPage(index)} 0 R`)
    .join(' ')

  const objects: string[] = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    `<< /Type /Pages /Kids [${kids}] /Count ${pages.length} >>`,
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>',
  ]

  pages.forEach((pageLines, index) => {
    const contentsObjectNumber = objectNumberForPage(index) + 1
    const content = pageContentStream(pageLines)
    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Resources << /Font << /F1 3 0 R >> >> /Contents ${contentsObjectNumber} 0 R >>`,
      `<< /Length ${content.length} >>\nstream\n${content}endstream`,
    )
  })

  let file = '%PDF-1.4\n'
  const offsets: number[] = []

  objects.forEach((body, index) => {
    offsets.push(file.length)
    file += `${index + 1} 0 obj\n${body}\nendobj\n`
  })

  const xrefOffset = file.length
  const pad = (offset: number): string => String(offset).padStart(10, '0')

  file += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`
  offsets.forEach((offset) => {
    file += `${pad(offset)} 00000 n \n`
  })
  file += `trailer\n<< /Size ${
    objects.length + 1
  } /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`

  return new Blob([file], {
    type: CARD_MANAGEMENT_STATEMENT_DOCUMENT_MEDIA.PDF.mediaType,
  })
}

/* -------------------------------------------------------------------------- */
/* CSV                                                                        */
/* -------------------------------------------------------------------------- */

/** UTF-8 byte order mark, so Excel opens the export without mangling accents. */
const BOM = String.fromCharCode(0xfeff)

/** RFC-4180 quoting: wrap any field containing a quote, comma, or newline. */
const toCsvRow = (cells: string[]): string =>
  cells
    .map((cell) =>
      /[",\n\r]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell,
    )
    .join(',')

/* -------------------------------------------------------------------------- */
/* Builder                                                                    */
/* -------------------------------------------------------------------------- */

export interface StatementDemoDocumentInput {
  statement: StatementSummary
  transactions: StatementTransaction[]
  format: StatementDocumentFormat
  /** "T&E Card •• 5678" — the same label the detail modal describes. */
  cardLabel: string
  /** "June 2026". */
  periodLabel: string
  /** The row's own status wording, already translated. */
  statusLabel: string
  /** "Jul 15, 2026". */
  dueLabel: string
  /** Renders a `Money` the way the surrounding screen renders it. */
  formatAmount: (money: Money) => string
  /** Renders a transaction's posting date, e.g. "Jun 3, 2026". */
  formatDate: (isoDate: string) => string
}

/**
 * Builds the demo document for one statement.
 *
 * Returns the same `CardManagementStatementDocument` shape the real download
 * resolves to, so the delivery step downstream cannot tell the two apart and
 * there is only one path that touches the DOM.
 *
 * @throws if asked for a format the demo cannot write (`XLSX`) — the callers
 *   offer only `DEMO_STATEMENT_DOCUMENT_FORMATS`, so reaching this is a bug
 *   rather than a user-facing state.
 */
export const buildStatementDemoDocument = ({
  statement,
  transactions,
  format,
  cardLabel,
  periodLabel,
  statusLabel,
  dueLabel,
  formatAmount,
  formatDate,
}: StatementDemoDocumentInput): CardManagementStatementDocument => {
  const fileName = `statement-${statement.period.slice(0, 7)}.${
    CARD_MANAGEMENT_STATEMENT_DOCUMENT_MEDIA[format].fileExtension
  }`

  if (format === 'CSV') {
    const rows = [
      toCsvRow(['Statement', periodLabel]),
      toCsvRow(['Card', cardLabel]),
      toCsvRow(['Status', statusLabel]),
      toCsvRow(['Payment due', dueLabel]),
      toCsvRow(['Statement amount', formatAmount(statement.statementAmount)]),
      toCsvRow(['Amount owed', formatAmount(statement.amountOwed)]),
      toCsvRow([SAMPLE_NOTICE]),
      '',
      toCsvRow(['Date', 'Description', 'Amount']),
      ...transactions.map((transaction) =>
        toCsvRow([
          formatDate(transaction.postedOn),
          transaction.description,
          formatAmount(transaction.amount),
        ]),
      ),
    ]

    return {
      format,
      fileName,
      blob: new Blob([BOM + rows.join('\r\n')], {
        type: `${CARD_MANAGEMENT_STATEMENT_DOCUMENT_MEDIA.CSV.mediaType};charset=utf-8;`,
      }),
    }
  }

  if (format === 'PDF') {
    const lines = [
      'Card Statement',
      SAMPLE_NOTICE,
      '',
      `Card: ${cardLabel}`,
      `Statement period: ${periodLabel}`,
      `Status: ${statusLabel}`,
      `Payment due: ${dueLabel}`,
      `Statement amount: ${formatAmount(statement.statementAmount)}`,
      `Amount owed: ${formatAmount(statement.amountOwed)}`,
      ...(statement.amountPaid
        ? [`Amount paid: ${formatAmount(statement.amountPaid)}`]
        : []),
      '',
      'Transactions',
      ...transactions.map(
        (transaction) =>
          `${formatDate(transaction.postedOn)}  ${
            transaction.description
          }  ${formatAmount(transaction.amount)}`,
      ),
    ]

    return { format, fileName, blob: renderPdf(lines) }
  }

  throw new Error(`Demo statement documents cannot be written as ${format}`)
}
